import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, canWrite, isSuperAdmin } from "@/lib/auth";
import { toast } from "sonner";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Gavel, Plus, RefreshCw, Search, ShieldAlert, CheckCircle2, XCircle, Undo2 } from "lucide-react";

const STATE_STYLE = {
  REQUESTED: { bg: "hsl(40 55% 94%)",  fg: "hsl(35 60% 28%)",  border: "hsl(40 55% 76%)" },
  APPROVED:  { bg: "hsl(150 45% 92%)", fg: "hsl(150 45% 24%)", border: "hsl(150 40% 66%)" },
  REJECTED:  { bg: "hsl(0 55% 94%)",   fg: "hsl(0 65% 30%)",   border: "hsl(0 55% 72%)" },
  EXPIRED:   { bg: "hsl(220 8% 94%)",  fg: "hsl(220 6% 40%)",  border: "hsl(220 6% 78%)" },
  REVOKED:   { bg: "hsl(220 8% 94%)",  fg: "hsl(220 6% 40%)",  border: "hsl(220 6% 78%)" },
};

function StateBadge({ value }) {
  const st = STATE_STYLE[value] || STATE_STYLE.REQUESTED;
  return <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em] font-semibold" style={{ background: st.bg, color: st.fg, borderColor: st.border }}>{value}</Badge>;
}

function CreateExceptionDialog({ vocab, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", written_reason: "", scope_summary: "",
    affected_record_type: "track", affected_record_id: "",
    blocked_condition_code: "OPERATIONAL_FRICTION",
    blocked_condition_text: "",
    duration_start: "", duration_end: "",
  });
  const submit = async () => {
    if (!form.title.trim() || !form.written_reason.trim() || !form.affected_record_id.trim()) {
      toast.error("Title, written reason, and affected record id are required.");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/commercial-exceptions", form);
      onCreated?.(r.data);
      toast.success("Commercial exception requested.");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed.");
    } finally { setSaving(false); }
  };
  const softCodes = vocab.soft_block_codes || ["OPERATIONAL_FRICTION", "OTHER"];
  const hardCodes = vocab.hard_blocks || [];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="ce-new" className="gap-2"><Plus className="w-4 h-4" /> Request exception</Button></DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Request Commercial Exception</DialogTitle>
          <DialogDescription>For non-critical operational friction only. Requests targeting DEMO_ONLY, known rights or legal failures, missing required filing evidence, or unauthorised restricted-material sharing will be refused.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Title</Label><Input value={form.title} onChange={(e)=>setForm({...form, title: e.target.value})} data-testid="ce-title" /></div>
          <div>
            <Label>Affected record type</Label>
            <Input value={form.affected_record_type} onChange={(e)=>setForm({...form, affected_record_type: e.target.value})} data-testid="ce-rec-type" placeholder="track / release_intel / metadata_field…" />
          </div>
          <div><Label>Affected record id</Label><Input value={form.affected_record_id} onChange={(e)=>setForm({...form, affected_record_id: e.target.value})} data-testid="ce-rec-id" /></div>
          <div>
            <Label>Blocked condition code</Label>
            <Select value={form.blocked_condition_code} onValueChange={(v)=>setForm({...form, blocked_condition_code: v})}>
              <SelectTrigger data-testid="ce-code"><SelectValue /></SelectTrigger>
              <SelectContent>
                {softCodes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                {hardCodes.map((c) => <SelectItem key={c} value={c} disabled>{c} — hard-block, cannot be overridden</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Blocked condition (plain-English)</Label><Input value={form.blocked_condition_text} onChange={(e)=>setForm({...form, blocked_condition_text: e.target.value})} /></div>
          <div><Label>Duration start</Label><Input value={form.duration_start} onChange={(e)=>setForm({...form, duration_start: e.target.value})} placeholder="2026-06-01" /></div>
          <div><Label>Duration end</Label><Input value={form.duration_end} onChange={(e)=>setForm({...form, duration_end: e.target.value})} placeholder="2026-07-01" /></div>
          <div className="col-span-2"><Label>Written reason</Label><Textarea rows={4} value={form.written_reason} onChange={(e)=>setForm({...form, written_reason: e.target.value})} data-testid="ce-reason" /></div>
          <div className="col-span-2"><Label>Scope summary</Label><Textarea rows={2} value={form.scope_summary} onChange={(e)=>setForm({...form, scope_summary: e.target.value})} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="ce-new-save">{saving ? "Saving…" : "Request"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CommercialExceptionsDesk() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [vocab, setVocab] = useState({});
  const mayWrite = canWrite(user);
  const canApprove = isSuperAdmin(user);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (q.trim()) params.q = q.trim();
      if (stateFilter) params.state = stateFilter;
      const r = await api.get("/commercial-exceptions", { params });
      setRows(r.data?.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed.");
    } finally { setLoading(false); }
  }, [q, stateFilter]);

  useEffect(() => {
    api.get("/commercial-exceptions/meta/vocab").then((r)=>setVocab(r.data || {})).catch(()=>{});
    load();
  }, [load]);

  const decide = async (cid, decision) => {
    const note = window.prompt(`Optional ${decision.toLowerCase()} note:`) || "";
    try {
      const r = await api.post(`/commercial-exceptions/${cid}/decision`, { decision, note });
      setRows((rs) => rs.map((row) => row.id === cid ? r.data : row));
      toast.success(`Exception ${decision.toLowerCase()}.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Decision failed.");
    }
  };

  const revoke = async (cid) => {
    const reason = window.prompt("Revocation reason (required):");
    if (!reason || !reason.trim()) return;
    try {
      const r = await api.post(`/commercial-exceptions/${cid}/revoke`, { reason: reason.trim() });
      setRows((rs) => rs.map((row) => row.id === cid ? r.data : row));
      toast.success("Exception revoked.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed.");
    }
  };

  if (!mayWrite) {
    return <div className="p-8 text-sm text-muted-foreground">Available only to authorised internal roles.</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="commercial-exceptions-desk">
      <div className="flex flex-wrap items-center gap-3">
        <Gavel className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Commercial Exceptions</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Human overrides for non-critical operational friction only. Never overrides DEMO_ONLY, known rights or legal failures, missing filing evidence, or unauthorised restricted-material sharing.
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">Internal-only</Badge>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="ce-refresh"><RefreshCw className="w-4 h-4" /> Refresh</Button>
        <CreateExceptionDialog vocab={vocab} onCreated={(x)=>setRows((rs)=>[x, ...rs])} />
      </div>

      <Card style={{ background: "hsl(0 55% 97%)", borderColor: "hsl(0 55% 72%)" }}>
        <CardContent className="py-3 text-sm flex items-center gap-2" style={{ color: "hsl(0 65% 30%)" }}>
          <ShieldAlert className="w-4 h-4" />
          Hard-blocks that cannot be overridden: <strong className="font-mono text-xs">{(vocab.hard_blocks || []).join(", ")}</strong>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Search className="w-4 h-4" /> Filters</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div><Label>Search</Label><Input value={q} onChange={(e)=>setQ(e.target.value)} data-testid="ce-filter-q" /></div>
            <div>
              <Label>State</Label>
              <Select value={stateFilter || "__any__"} onValueChange={(v)=>setStateFilter(v === "__any__" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any</SelectItem>
                  {(vocab.states || []).map((k)=><SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end"><Button className="w-full" onClick={load} data-testid="ce-filter-apply">Apply</Button></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Exceptions ({rows.length})</CardTitle>
          <CardDescription>Only Super Admin may approve, reject, or revoke.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-muted-foreground">Loading…</p> :
            rows.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="ce-empty">No exceptions recorded.</p> :
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.id} className="border border-border rounded-md px-4 py-3" data-testid={`ce-row-${r.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <StateBadge value={r.state} />
                    <span className="font-semibold text-sm flex-1">{r.title}</span>
                    {canApprove && r.state === "REQUESTED" && (
                      <>
                        <Button size="sm" onClick={()=>decide(r.id, "APPROVED")} className="gap-1" data-testid={`ce-approve-${r.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Approve</Button>
                        <Button size="sm" variant="outline" onClick={()=>decide(r.id, "REJECTED")} className="gap-1" data-testid={`ce-reject-${r.id}`}><XCircle className="w-3.5 h-3.5" /> Reject</Button>
                      </>
                    )}
                    {canApprove && r.state === "APPROVED" && (
                      <Button size="sm" variant="outline" onClick={()=>revoke(r.id)} className="gap-1" data-testid={`ce-revoke-${r.id}`}><Undo2 className="w-3.5 h-3.5" /> Revoke</Button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mt-2">
                    <div><span className="opacity-60">Affected type:</span> {r.affected_record_type || "—"}</div>
                    <div><span className="opacity-60">Affected id:</span> <span className="font-mono">{r.affected_record_id || "—"}</span></div>
                    <div><span className="opacity-60">Block code:</span> {r.blocked_condition_code || "—"}</div>
                    <div><span className="opacity-60">Requested by:</span> {r.requested_by_email || "—"}</div>
                    <div><span className="opacity-60">Approver:</span> {r.approver_email || "—"}</div>
                    <div><span className="opacity-60">Approved at:</span> {r.approved_at || "—"}</div>
                    <div><span className="opacity-60">Duration:</span> {r.duration_start || "—"} → {r.duration_end || "—"}</div>
                    <div><span className="opacity-60">Proof ledger id:</span> <span className="font-mono text-[10px]">{r.proof_ledger_audit_id || "—"}</span></div>
                  </div>
                  <div className="text-xs mt-2"><span className="opacity-60">Reason:</span> {r.written_reason}</div>
                  {r.rejection_reason && <div className="text-xs mt-1"><span className="opacity-60">Rejection note:</span> {r.rejection_reason}</div>}
                  {r.revocation_reason && <div className="text-xs mt-1"><span className="opacity-60">Revocation reason:</span> {r.revocation_reason}</div>}
                </div>
              ))}
            </div>
          }
        </CardContent>
      </Card>
    </div>
  );
}
